import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { AbstractControl, UntypedFormGroup, Validators } from "@angular/forms";
import { animate, style, transition, trigger } from "@angular/animations";
import { ActivatedRoute, Router } from "@angular/router";
import {
  IAnswer,
  IFormField,
  makeControlForQuestion
} from "../../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { Subscription } from "rxjs";
import { PopulationRulesService } from "./population-rules.service";
import {
  ClientAnswerService,
  ClientAnswerBulkTableAnswersInner,
  ClientAnswerBulk,
  ClientAnswerBulkAnswersInner,
  PopulationRule,
  PopulationService,
} from "../../../../../../swaggerServices";
import { PopulationRulesResolver } from "./population-rules-resolver.service";
import { Answer } from "../../../../../../swaggerServices";
import { OrderByPipe } from "../../../../../shared/pipes/order-by.pipe";
import { cloneDeep } from "lodash-es";
import { UserInfoService } from "../../../../user-info.service";
import { ClientRouteResolver } from "../../client-route-resolver.service";
import { ConfigStateService } from "../../../../config/config-state.service";
import { Tenant } from "../../../../../shared/types/tenant.enum";

declare var $: any;

interface IClientAnswer extends Answer {
  isGlobal?: boolean
}

interface IClientQuestion extends IFormField {
  clientHint: String
  clientAnswers: IClientAnswer[]
  answers: any;
  clientQuestionDropdowns: any[]
  questions?: IClientQuestion[]; // column q for tables
  rows?: any[]
}

interface ICategoryRule {  // category specific rules
  id: number
  name: string
  pageNum?: number
  clientQuestions: IClientQuestion[]
}

interface IPopulationRule extends PopulationRule {
  populationClientRules: ICategoryRule[]
  questions: IClientQuestion[] // all q in all categories
  questionsById: { [id: string]: IClientQuestion } // all q in all categories
  clientId?: number,
  countryId: number
}
interface IClientAnswerBulkAnswers extends ClientAnswerBulkAnswersInner {
  isGlobal?: boolean
  populationId?: number
}

interface ITableQuestion extends IFormField {
  name?: string,
  isTable?: boolean,
  questions?: IFormField[],
  rows?: {
    rowAnswer: any,
    cells: {}
  }[]
}

@Component({
    selector: "app-population-rules",
    templateUrl: "./population-rules.component.html",
    styleUrls: ["./population-rules.component.scss"],
    animations: [
        trigger('enterAnimation', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('500ms', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                style({ opacity: 1 }),
                animate('500ms', style({ opacity: 0 }))
            ])
        ])
    ],
    host: {
        "[class.readonly]": "readOnly"
    },
    standalone: false
})
export class PopulationRulesComponent implements OnInit, OnDestroy {
  formGroup: UntypedFormGroup;
  activeField: IClientQuestion;
  clickedField: boolean;
  subscriptions: Subscription[] = [];
  routeSub: Subscription;
  populationRules: IPopulationRule; // all category rules
  categoryRules: ICategoryRule;
  unsavedControls: number = 0;
  formChanges: {} = {}; // list of all changed form controls
  globalAnsChanges: {} = {}; // list of question which has changed global ans toggle
  @ViewChild('saveModal') saveModal: ElementRef;
  isSaving: boolean;
  originalData;
  readOnly: boolean = !this.userInfo.isClientAdmin;
  clickedCell: boolean;
  client?: any;
  showHintBox: boolean;
  showQuestionId: boolean;
  showHiddenQuestions: boolean = false;
  inputQuestionId: number;
  lastScrollFragment?: string;
  userAccessCountriesIds = [];
  hasCountryAccess: boolean;

  // sets answer as global which is applied to same question across all populations for client
  setGlobal: Function = (question: IClientQuestion) => {
    const q = this.populationRules.questionsById[question.id];
    // global setting is saved at the question level
    if (q.displayFieldType === 'table') {
      const newGlobal = !q.isGlobal;
      q.isGlobal = newGlobal;
      this.globalAnsChanges[q.id] = newGlobal;
    } else {
      // existing answer
      if (q.clientAnswers[0]) {
        const newGlobal = !q.clientAnswers[0].isGlobal;
        q.clientAnswers[0].isGlobal = newGlobal;
        this.globalAnsChanges[q.id] = newGlobal;
      } else {
        // new answer
        if (q.clientAnswers.length === 0) {
          q.clientAnswers.push({
            isGlobal: true,
            answer: this.formGroup.get(String(q.id)) && this.formGroup.get(String(q.id)).value
              ? String(this.formGroup.get(String(q.id)).value)
              : ""
          });
        }

        this.globalAnsChanges[q.id] = true;
      }
    }
    this.countUnsavedFields();
  };

  constructor(private activatedRoute: ActivatedRoute,
    private router: Router,
    private populationRulesService: PopulationRulesService,
    private populationRulesResolver: PopulationRulesResolver,
    private clientAnswerService: ClientAnswerService,
    private orderByPipe: OrderByPipe,
    public userInfo: UserInfoService,
    private config: ConfigStateService,
    public clientRouteResolver: ClientRouteResolver,
    private populationService: PopulationService
  ) {
  }

  ngOnInit() {

    // subscribe to population fetch changes and re-init forms for new population
    this.routeSub = this.populationRulesResolver.populationRule().subscribe((data: any) => {
      this.init(data);
    });
    this.subscriptions.push(this.config.loadConfig().subscribe(() => {}));

  }

  init(populationRules) {
    this.inputQuestionId = Number(this.activatedRoute.snapshot.queryParams?.question)
    this.subscriptions.forEach(s => s?.unsubscribe());
    this.subscriptions = [];

    this.populationRules = populationRules;
    this.originalData = cloneDeep(populationRules);
    this.initCountryAccess();

    // sort by order
    this.populationRules.populationClientRules = this.orderByPipe.transform(this.populationRules.populationClientRules, 'order');
    this.formGroup = new UntypedFormGroup({});
    this.formChanges = {};
    this.globalAnsChanges = {};
    this.showQuestionId = this.config.tenant !== Tenant.AU;
    this.readOnly = !this.canEditClientRules();

    this.populationRules.populationClientRules.forEach(c => {

      c.clientQuestions.forEach(q => {

        // associate client specific models to generic models for form wrapper
        if (!q.clientAnswers) q['clientAnswers'] = [];
        q.answers = q.clientAnswers;
        if (q.clientQuestionDropdowns) q.questionDropdowns = q.clientQuestionDropdowns;

        // tb column questions added later
        if (q.displayFieldType !== 'table' && !q.parentId) {
          const control = makeControlForQuestion(q);
          if (control) {
            this.formGroup.addControl(q.id.toString(), control);
            if (control.dirty && !this.formGroup.dirty) this.formGroup.markAsDirty();
          }
        }

      });

    });

    this.populationRulesService.initOriginalRules(this.originalData);
    this.populationRulesService.initPopulationRulesChanges(this.formGroup, this.globalAnsChanges);

    // append page numbers
    this.populationRules.populationClientRules.forEach((c, i) => c.pageNum = i + 1);

    // map full questions and answers
    this.initGlobalCategory();
    this.initTables();
    this.initQuestions();
    this.initDependantMandatory();
    this.handleDeselects();

    // update total unsaved questions count
    this.populationRulesService.initUnsavedControls(this.formChanges, this.unsavedControls);

    this.subscriptions.push(this.formGroup.valueChanges.subscribe(val => this.handleFormChanges(val)));
    this.countUnsavedFields();

    // filter questions for selected category
    this.subscriptions.push(this.activatedRoute.queryParams.subscribe(params => {
      // activate first category menu
      if (!params.category)
        this.router.navigate(["clients", this.populationRules.clientId, "populations", this.populationRules.id, "rules"], {
          queryParams: {
            category: this.populationRules.populationClientRules[0].id
          }
        });
      else this.getCategoryRules(Number(params.category));
    }));

    this.subscriptions.push(this.clientRouteResolver.client().subscribe((client: any) => {
      this.client = client;
    }));

    setTimeout(() => {
      if (this.inputQuestionId) {
        this.scrollToSegment(this.inputQuestionId)
      }
    }, 0);

  }

  scrollToSegment(fragment) {
    if ( fragment && fragment !== this.lastScrollFragment) {
      let el;
      this.categoryRules.clientQuestions.map(q=>{
        if (q.id === fragment) {
          if (q.parentId) {
            el = $("#question-" + q.parentId).get(0);
          }else {
            el = $("#question-" + fragment).get(0);
          }
        }
      })
      if (el) {
        el.scrollIntoView(true);
        this.lastScrollFragment = fragment;
      }
    }
  }

  canEditClientRules() {
    if (this.config.featureFlags.clientRulesEditableByUsers) {
      return this.hasCountryAccess;
    }
    return this.userInfo.isClientAdmin && this.hasCountryAccess;
  }

  // If the population has country level access OR is unassigned i.e. countryId is null
  initCountryAccess() {
    this.hasCountryAccess = this.userInfo.countries.find(c => c.id === this.populationRules.countryId) ? true : false  || !this.populationRules.countryId;
  }

  // unassigned/base populations countryId is null
  isUnassignedPopulation() {
    return !this.populationRules.countryId;
  }

  handleFormChanges(val) {

    Object.keys(this.formGroup.controls).forEach(key => {

      let questionId;
      let tblQ;
      let rowIndex: number;

      // table row questions have id format of tableQuestionId-questionId (eg 123-2). extract the table question id
      if (key.indexOf("-") !== -1) {
        questionId = Number(key.substr(0, key.indexOf("-"))); // column question id
        rowIndex = Number(key.substr(key.indexOf("-") + 1));
      } else {
        questionId = Number(key);
      }

      this.originalData.populationClientRules.forEach(c => {
        const question = c.clientQuestions.find(q => q.id === questionId);

        if (question) {
          let oldVal;
          if (question.parentId) {
            tblQ = this.populationRules.questionsById[question.parentId];
            oldVal = tblQ.rows[rowIndex].cells[question.id].answer;
          } else {
            oldVal = (question.clientAnswers[0] ? question.clientAnswers[0].answer || "" : "").toString();
          }

          let newVal = val[key];
          // note: number fields convert empty values to null
          newVal = newVal == null ? newVal = "" : newVal.toString();

          this.formChanges[key] = oldVal !== newVal;
        }

      });

    });

    this.countUnsavedFields();
  };

  // counts total amount of unsaved answer changes including global answer toggle
  countUnsavedFields() {
    this.unsavedControls = 0;

    Object.keys(this.formChanges).forEach(key => {
      if (this.formChanges[key] !== false) {
        this.unsavedControls++;
      }
    });


    Object.keys(this.globalAnsChanges).forEach(key => {
      const newGlobalValue = this.globalAnsChanges[key];

      const origQ = this.populationRulesService.getQuestion(Number(key));

      if (origQ.displayFieldType === 'table') {
        // table with existing rows
        if (origQ.clientAnswers[0] && origQ.clientAnswers[0].isGlobal !== newGlobalValue) {
          this.unsavedControls++;
        }
      } else {
        // only if it doesn't already have ans changes
        if (!this.formChanges[Number(key)]) {
          if (!origQ.clientAnswers[0]) {
            // no existing ans
            if (newGlobalValue) {
              this.unsavedControls++;
            }
          } else {
            // existing ans
            if (origQ.clientAnswers[0].isGlobal !== newGlobalValue) {
              this.unsavedControls++;
            }
          }
        }
      }
    });

  }


  // generate global category list
  initGlobalCategory() {

    let globalCat: ICategoryRule = {
      id: null,
      name: "Global Fields",
      clientQuestions: []
    };

    this.populationRules.populationClientRules.forEach(c => {
      c.clientQuestions.forEach(q => {
        if ((q.displayFieldType === 'table' && q.isGlobal) || (q.clientAnswers[0] && q.clientAnswers[0].isGlobal)) {
          globalCat.clientQuestions.push(q);
        }
      });

    });

    this.populationRules.populationClientRules.push(globalCat);

  }

  // create array of all questions
  initQuestions() {
    this.populationRules.questions = [];
    this.populationRules.populationClientRules.forEach(c =>
      c.clientQuestions.forEach(q =>
        !this.populationRules.questions.find(_q => _q.id === q.id) ? this.populationRules.questions.push(q) : false)
    );
    this.populationRules.questionsById = {};
    this.populationRules.questions.forEach(q => {
      this.populationRules.questionsById[q.id] = q;
    });
  }


  initDependantMandatory() {
    this.populationRules.questions.forEach((q: IFormField) => {
      if (q.dependantMandatoryParentId) {

        // get form control of dependant question
        const depQ: AbstractControl = this.formGroup.get(String(q.dependantMandatoryParentId));
        // current field's form control
        const formControl: AbstractControl = this.formGroup.get(String(q.id));

        // init on first load
        // match any value
        if (q.dependantMandatoryParentValue === null && depQ?.value?.length && q.validators) {
          q.mandatory = true;
          formControl.setValidators(Validators.compose(q.validators?.concat([Validators.required])));
          formControl.updateValueAndValidity();
        }
        // match certain value
        if (q.dependantMandatoryParentValue !== null && String(depQ?.value) === String(q.dependantMandatoryParentValue)) {
          q.mandatory = true;
          formControl.setValidators(Validators.compose(q.validators?.concat([Validators.required])));
          formControl.updateValueAndValidity();
        }

        // update dependant mandatory on value change
        this.subscriptions.push(depQ?.valueChanges.subscribe(v => {

          // when dependency is 'any value'
          if (q.dependantMandatoryParentValue === null && v?.length) {
            q.mandatory = true;
            formControl.setValidators(Validators.compose(q.validators.concat([Validators.required])));
            this.populationRulesService.dependantMandatoryChanged.next({
              mandatory: true,
              question: q
            });
          }

          else if (q.dependantMandatoryParentValue !== null && String(v) === String(q.dependantMandatoryParentValue)) {
            q.mandatory = true;
            formControl.setValidators(Validators.compose(q.validators.concat([Validators.required])));
            this.populationRulesService.dependantMandatoryChanged.next({
              mandatory: true,
              question: q
            });
          } else {
            q.mandatory = false;
            formControl.setValidators(Validators.compose(q.validators));
            this.populationRulesService.dependantMandatoryChanged.next({
              mandatory: false,
              question: q
            });
          }
          formControl.updateValueAndValidity();
        }));

      }

    });

  }

  // activates hint text for selected question
  selectField(field: IClientQuestion) {
    if (this.readOnly || field === this.activeField) {
      return;
    }
    this.activeField = field;
    this.clickedField = true;
    this.showHintBox = !!field.clientHint;
    setTimeout(() => this.clickedField = false);
  }

  handleCommasInCells(line: string) {
    const re_valid = /^\s*(?:'[^'\\]*(?:\\[\S\s][^'\\]*)*'|"[^"\\]*(?:\\[\S\s][^"\\]*)*"|[^,'"\s\\]*(?:\s+[^,'"\s\\]+)*)\s*(?:,\s*(?:'[^'\\]*(?:\\[\S\s][^'\\]*)*'|"[^"\\]*(?:\\[\S\s][^"\\]*)*"|[^,'"\s\\]*(?:\s+[^,'"\s\\]+)*)\s*)*$/;
    const re_value = /(?!\s*$)\s*(?:'([^'\\]*(?:\\[\S\s][^'\\]*)*)'|"([^"\\]*(?:\\[\S\s][^"\\]*)*)"|([^,'"\s\\]*(?:\s+[^,'"\s\\]+)*))\s*(?:,|$)/g;
    // Return NULL if input string is not well formed CSV string.
    if (!re_valid.test(line)) return null;
    const a = [];                     // Initialize array to receive values.
    line.replace(re_value, // "Walk" the string using replace with callback.
        (m0, m1, m2, m3) => {
            if (m1 !== undefined) a.push(m1.replace(/\\'/g, "'")); // Remove backslash from \' in single quoted values.
            else if (m2 !== undefined) a.push(m2.replace(/\\"/g, '"')); // Remove backslash from \" in double quoted values.
            else if (m3 !== undefined) a.push(m3);
            return ''; // Return empty string.
        });
    if (/,\s*$/.test(line)) a.push('');  // Handle special case of empty last value.
    return a;
  };

  upload(file: any, question) {
    // read csv file
    let reader: FileReader = new FileReader();
    reader.readAsText(file[0]);
    const jsonData = [];
    reader.onload = e => {
      // convert csv file to json format
      let csv: string = reader.result as string;
      let lines = csv.split("\n");
      const headers = lines[0].split(",");
      for (let i = 1; i < lines.length && lines[i] !== ""; i++) {
        const obj = {};
        let currentLine = lines[i].replace("\r", "").split(",");
        if (currentLine.length !== headers.length) {
          currentLine = this.handleCommasInCells(lines[i])
        }
        for (let j = 0; j < headers.length; j++) {
          obj[headers[j].split("\r").join("")] = currentLine[j]
            .split('"')
            .join("");
        }
        jsonData.push(obj);
      }
      //update answer
      jsonData.forEach((data, index) => {
        const keys = Object.keys(data);
        keys.forEach(key => {
          if (index >= question.rows.length) {
            this.newRow(question);
          }
          const columnQ = question.questions.find(q => q.question === key);
          if (columnQ) {
            const control = this.getFormControl(columnQ, index);
            if (control) control.setValue(data[key]);
          }
        });
      });
    };
  }

  download(question: any) {
    this.populationService.downloadTable(this.populationRules.id, { question: question } ).subscribe((data: any) => {

      //transfer json to csv format
      const replacer = (key, value) => value === null ? '' : value // specify how you want to handle null values here
      const header = Object.keys(data.fileContent[0])
      let csv = data.fileContent.map(row => header.map(fieldName =>
      JSON.stringify(row[fieldName], replacer)).join(','))
      csv.unshift(header.join(','))
      csv = csv.join('\r\n')
      csv = "\ufeff" + csv

      // save csv file
      var elem = window.document.createElement('a');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-16' });
      const url = window.URL.createObjectURL(blob);
      elem.href = url
      elem.download = data.fileName ? data.fileName.replace('.csv', '') : "package";
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    })
  }


  // saves all changed answers
  save() {
    this.isSaving = true;

    // check for changed input values
    const answers: IClientAnswerBulkAnswers[] = [];
    Object.keys(this.formChanges).forEach(key => {

      // ignore column questions
      if (key.indexOf("-") !== -1) return false;

      if (this.formChanges[key] === false) return false;

      // get isGlobal from answer
      const q = this.populationRules.questionsById[Number(key)];
      const isGlobal = q.clientAnswers[0] && q.clientAnswers[0].isGlobal || false;

      let req: IClientAnswerBulkAnswers = {
        populationId: this.populationRules.id,
        clientQuestionId: Number(key),
        answer: this.formGroup.get(String(key)) && this.formGroup.get(String(key)).value != null
          ? String(this.formGroup.get(String(key)).value)
          : "",
        isGlobal: key in this.globalAnsChanges ? this.globalAnsChanges[key] : isGlobal
      };

      // note: only append id if updating existing answer, otherwise no id if adding answer
      if (q.clientAnswers[0] && q.clientAnswers[0].id) req['id'] = q.clientAnswers[0].id;
      answers.push(req);

    });

    const tableAnswers = [];
    const handleTableAnswers = (tblQ) => {

      // don't dup table ans, this can occur when table column has changes, and when table isGlobal has changed
      const tableAns = tableAnswers.find(ta => ta.questionId === tblQ.id);
      if (tableAns) return false;

      const rows = [];
      tblQ.rows.forEach((r, rIndex) => {
        const cells = [];
        Object.keys(r.cells).forEach((columnQuestionId, index) => {
          const columnQ = tblQ.questions.find(q => q.id === Number(columnQuestionId));
          const formControl = this.getFormControl(columnQ, rIndex);
          cells.push({
            clientQuestionId: columnQ.id,
            answer: formControl ? formControl.value != null
              ? formControl.value.toString() : ""
              : r.cells[columnQuestionId].answer,
          });
        });

        // don't save empty rows
        if (!cells.every(c => c.answer === "")) {
          rows.push({
            cells: cells
          });
        }

      });

      const tblAns: ClientAnswerBulkTableAnswersInner = {
        questionId: tblQ.id,
        global: tblQ.isGlobal,
        rows: rows
      };

      tableAnswers.push(tblAns);

    };


    Object.keys(this.formChanges).forEach(key => {
      // table row questions have id format of tableQuestionId-questionId (eg 123-2). extract the table question id
      if (key.indexOf("-") !== -1) {
        // only save if it has changed
        if (this.formChanges[key]) {
          const questionId = Number(key.substr(0, key.indexOf("-"))); // column question id
          const columnQ = this.populationRules.questionsById[questionId];
          const tblQ = this.populationRules.questionsById[columnQ.parentId];
          handleTableAnswers(tblQ);
        }
      }
    });

    // check for changed 'global answer' toggle
    Object.keys(this.globalAnsChanges).forEach(key => {
      const origQ = this.populationRulesService.getQuestion(Number(key));

      if (origQ.displayFieldType === 'table') {
        const q = this.populationRules.questionsById[Number(key)];
        handleTableAnswers(q);
      } else {
        if (!origQ.clientAnswers[0] || this.globalAnsChanges[key] !== origQ.clientAnswers[0].isGlobal) {
          // check if ans obj already exists in answer req array
          const ans = answers.find(a => a.clientQuestionId === origQ.id && a.populationId === this.populationRules.id);
          if (!ans) {
            const req: IClientAnswerBulkAnswers = {
              populationId: this.populationRules.id,
              clientQuestionId: Number(key),
              answer: this.formGroup.get(String(key)) && this.formGroup.get(String(key)).value
                ? String(this.formGroup.get(String(key)).value)
                : "",
              isGlobal: this.globalAnsChanges[key]
            };
            if (origQ.clientAnswers[0] && origQ.clientAnswers[0].id) req['id'] = origQ.clientAnswers[0].id;
            answers.push(req);
          }
        }
      }
    });

    let body = {
      populationId: this.populationRules.id,
      answers: answers.length > 0 ? answers : undefined,
      tableAnswers: tableAnswers.length > 0 ? tableAnswers : undefined
    };

    this.clientAnswerService.bulkCreateUpdateClientAnswer(body).subscribe(() => {
      this.populationRulesResolver.resolveForPopulation(this.populationRules.id).subscribe(
        (data) => {
          this.init(data);
          this.isSaving = false;
        });
    });

  }

  // gets the specific population rule
  // global category has null as id
  getCategoryRules(categoryId: number) {
    categoryId
      ? this.categoryRules = this.populationRules.populationClientRules.find(c => c.id === Number(categoryId))
      : this.categoryRules = this.populationRules.populationClientRules.find(c => c.id === null)
  }

  // function to pass to router de-activator to show alert for unsaved field changes
  // when in readonly mode, allow switching population without prompt
  hasChanges() {
    return !this.readOnly && this.unsavedControls > 0
      ? confirm(`You have ${this.unsavedControls} unsaved changes. Are you sure you want to navigate away from this page?`)
      : true
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s?.unsubscribe());
    this.routeSub.unsubscribe();
  }

  // determine is a question contains a global answer
  hasGlobalAnswer(q: IClientQuestion) {
    return q.displayFieldType === 'table' ? q.isGlobal : q.answers[0] && q.answers[0].isGlobal;
  }

  initTables() {

    this.populationRules.populationClientRules.forEach(c => {
      if (!c.id) return false; // ignore global category

      c.clientQuestions.forEach((q) => {

        if (q.displayFieldType === 'table') {

          q.questions = [];
          // determine if tbl q is global
          q.isGlobal = q.clientAnswers && q.clientAnswers.length > 0 && q.clientAnswers[0].isGlobal;

          // find the parent table question and its column questions
          c.clientQuestions.forEach(_q => {
            if (_q.parentId === q.id) {
              if (_q.displayFieldType === 'checkbox' && _q.dataType === 'boolean') {
                q.questions.unshift(_q);
              } else {
                q.questions.push(_q);
              }
            }
          });

          q.answers = q.answers.sort((a, b) => a.id - b.id);
          q.rows = q.answers.map((rowAnswer) => {
            const cellsByColQuestionId = {};
            q.questions.forEach(colQ => {
              const cellAnswer = colQ.answers.find(a => a.parentClientAnswerId === rowAnswer.id);
              cellsByColQuestionId[colQ.id] = cellAnswer || { answer: "" };
            });
            return {
              rowAnswer,
              cells: cellsByColQuestionId
            };
          });

          // init form ctrl for each tble row cell - MOVED TO onclick
          // q.questions.forEach(colQ => {
          //   q.rows.forEach((row, rowIndex) => {
          //     const cellAnswer = row.cells[colQ.id];
          //     const control = makeControlForQuestion(colQ, true, cellAnswer);
          //     this.formGroup.addControl(`${colQ.id}-${rowIndex}`, control);
          //     if (control.dirty && !this.formGroup.dirty) this.formGroup.markAsDirty();
          //   });
          // });

        }

      });

    });

    setTimeout(() => {
      $("table").each((index, table) => {
        $(table).floatThead({
          zIndex: 9,
          scrollContainer: () => $(table).closest(".table-container"),
          responsiveContainer: () => $(table).closest(".table-container")
        });
      });
      $(".table-label .globe-icon").popup({
        position: "top center",
        inline: false,
        variation: "inverted",
        delay: {
          show: 500
        }
      });
    });

  }

  isBooleanCheckboxColumn(q: IFormField) {
    return q.displayFieldType === 'checkbox' && q.dataType === 'boolean';
  }

  getTableCells(rowIndex: number, tableQ) {
    const row = tableQ.rows[rowIndex];
    const cells = tableQ.questions.filter(q => !q.hidden).map(columnQuestion => {
      const cellAnswer = row.cells[columnQuestion.id];
      const control = this.getFormControl(columnQuestion, rowIndex);
      const cell = {
        question: columnQuestion,
        answer: cellAnswer,
        formControl: control,
        displayAnswer: ''
      };
      cell.displayAnswer = this.getDisplayAnswer(cell);
      return cell;
    });
    return cells;
  }

  tableCellsTrackBy(index: number, cell) {
    return cell.question.id;
  }

  getDisplayAnswer(cell) {
    const q = cell.question;
    const answerValue = cell.formControl ? cell.formControl.value : cell.answer.answer;
    if (q.displayFieldType === "drop down" || q.displayFieldType === "people") {
      const selectedDropdown = answerValue ? q.questionDropdowns.find(qd => qd.value === answerValue) : null;
      return selectedDropdown != null && selectedDropdown.display || answerValue;
    }
    return answerValue;
  }

  // focus on input when selecting a table cell
  focusTableInput: Function = (tableQ: ITableQuestion, cell, rowIndex: number) => {
    let control = this.getFormControl(cell.question, rowIndex);
    if (!control) {
      const control = makeControlForQuestion(cell.question, true, cell.answer);
      this.formGroup.addControl(`${cell.question.id}-${rowIndex}`, control);
      if (control.dirty && !this.formGroup.dirty) this.formGroup.markAsDirty();
    }
    if (this.isBooleanCheckboxColumn(cell.question as IFormField)) {
      control = this.getFormControl(cell.question, rowIndex);
      control.setValue(control.value === 'true' ? '' : 'true');
    } else {
      this.clickedCell = true; // prevent unfocusTableCells() causing cell to de-activate
      setTimeout(() => this.clickedCell = false);
      const tableQId = tableQ.id;
      const el = $(`#cell-${tableQId}-${cell.question.id}-${rowIndex}`);
      setTimeout(() => tableQ.rows[rowIndex].cells[cell.question.id].isActive = true);
      if (cell.question.displayFieldType === 'text' || cell.question.displayFieldType === 'textarea')
        setTimeout(() => el.find('input').focus());
    }
    setTimeout(() => {
      const q: any = cell.question;
      this.selectField(q);
    });
  };

  // appends new table row
  newRow: Function = (question: ITableQuestion) => {

    // new row answer
    const newRow = { rowAnswer: null, cells: {} };
    const rowIndex = question.rows.length;
    question.rows.push(newRow);

    question.questions.forEach(q => {
      const newAnswer = {
        answer: "",
        isNew: true
      };
      newRow.cells[q.id] = newAnswer;
      const control = makeControlForQuestion(q, true);
      // NOTE(mike): this is registerControl() and not addControl() because add will trigger the valueChanges subscription.
      // Call updateValueAndValidity() at the end to trigger the subscription after all the controls have been added
      this.formGroup.registerControl(`${q.id}-${rowIndex}`, control);
      if (control.dirty) {
        this.formGroup.markAsDirty();
      }
    });
    this.formGroup.updateValueAndValidity();
  };

  // gets the form control
  getFormControl: Function = (q: IFormField, rowIndex?: number): AbstractControl => {
    return rowIndex != null ? this.formGroup.get(`${q.id}-${rowIndex}`) : this.formGroup.controls[q.id];
  };

  // deselects active table rows cells in focus
  handleDeselects() {
    setTimeout(() =>
      $('body').click(() => !this.clickedCell ? this.unfocusTableCells() : undefined));
  }

  // deselects any active row and cell
  unfocusTableCells() {
    this.populationRules.populationClientRules.forEach(c => {
      c.clientQuestions.forEach((q) => {
        if (q.displayFieldType === 'table') {
          q.rows.forEach(row => {
            Object.values(row.cells).forEach((a: IAnswer) => {
              if (a.isActive) {
                a.isActive = false;
              }
            });
          });
        }
      });
    });
  };

  toggleHiddenQuestions() {
    if (this.userInfo.isAdmin) {
      this.showHiddenQuestions = !this.showHiddenQuestions;
    }
  }

}
